/**
 * F29 (Wave 2C) — Admin team detail + moderation page.
 *
 * Route: /teams/:teamId
 * Permission: teams:read (super_admin + support + content_moderator)
 *
 * Moderation actions ([Suspend] / [Reinstate] / [Disband]) are additionally
 * gated client-side on `teams:moderate` (super_admin + content_moderator);
 * the server re-enforces.
 *
 * Composition:
 *   - Header card: TeamCard (read-only) + status banner (suspended/disbanded)
 *   - Roster: members + role + verified state
 *   - Audit-log timeline (member-visible per D9 — admin sees the same rows)
 *   - Action bar: state-aware Suspend / Reinstate / Disband
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  type AuditEvent,
  Banner,
  Button,
  Card,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  TeamCard,
  Avatar,
  useToast,
} from '@bravemove/ui-web';
import { UIIcon } from '@bravemove/design-tokens/icons';
import type {
  DisbandTeamByAdminInput,
  ReinstateTeamInput,
  SuspendTeamInput,
  TeamAuditEntry,
  TeamMemberWithUser,
  TeamStatus,
} from '@bravemove/types';
import { adminTeamsApi, type AdminTeamDetailResponse } from '../../api/teams.js';
import { translateApiError } from '../../auth/errors.js';
import { ApiError } from '../../auth/types.js';
import { useAuth } from '../../auth/useAuth.js';
import { SuspendTeamDialog } from '../../components/teams/SuspendTeamDialog.js';
import { DisbandTeamDialog } from '../../components/teams/DisbandTeamDialog.js';
import { ReinstateTeamDialog } from '../../components/teams/ReinstateTeamDialog.js';
import { InertTick } from '../../components/verification/EntityVerificationCard';

const STATUS_CHIP_MAP: Record<TeamStatus, StatusChipStatus> = {
  active: 'approved',
  suspended: 'in_review',
  disbanded: 'rejected',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

/** Map TeamAuditEntry → AuditLogTimeline AuditEvent. */
function mapAuditEntry(
  e: TeamAuditEntry,
  // Match i18next's `t` shape so the call sites pass `t` directly.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any,
  locale: string,
): AuditEvent {
  // Coarse kind classification: anything "removed/disbanded/suspended" → delete,
  // "issued/issued/accepted/transferred" → create, "updated/privacy_changed/
  // join_policy_changed/photo_uploaded" → update, "withdrawn/declined/denied"
  // → permission, default → info.
  const action = e.action;
  let kind: AuditEvent['kind'] = 'info';
  if (
    action.includes('removed') ||
    action.includes('disbanded') ||
    action.endsWith('.left') ||
    action.endsWith('.withdrawn') ||
    action.endsWith('.declined') ||
    action.endsWith('.denied')
  ) {
    kind = 'delete';
  } else if (action === 'team.suspended') {
    kind = 'alert';
  } else if (action === 'team.reinstated' || action === 'team.created') {
    kind = 'create';
  } else if (
    action.includes('updated') ||
    action.includes('changed') ||
    action.includes('photo_uploaded')
  ) {
    kind = 'update';
  } else if (action.startsWith('invitation.') || action.startsWith('join_request.')) {
    kind = 'update';
  } else if (action.includes('captaincy.transferred')) {
    kind = 'permission';
  }

  const actorName =
    e.actorKind === 'admin'
      ? t('admin:team.admin.detail.audit_actor_admin')
      : e.actorKind === 'system'
        ? t('admin:team.admin.detail.audit_actor_system')
        : ((e.payload as { actorDisplayName?: string }).actorDisplayName ?? e.actorKind);

  const event: AuditEvent = {
    id: e.id,
    at: formatDate(e.createdAt, locale),
    kind,
    summary: t(`team:audit_action.${e.action}`, { defaultValue: e.action }),
    actor: { name: actorName },
  };
  if (e.targetKind) {
    return { ...event, targetLabel: e.targetKind };
  }
  return event;
}

function RosterRow({
  member,
  t,
}: {
  member: TeamMemberWithUser;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: any;
}) {
  return (
    <li className="flex items-center gap-3 py-2 border-b border-hairline last:border-b-0">
      <Avatar name={member.displayName} src={member.avatarUrl ?? undefined} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-ink truncate">{member.displayName}</span>
          {/* F09 v2 (§13 row 7) — the member's player tick, inert, from the
              boolean only (the hand-drawn pill keyed on `verifiedAt` is gone). */}
          <InertTick
            verified={member.verified}
            entityType="player"
            testId={`team-roster-tick-${member.userId}`}
          />
        </div>
        <div className="text-[11.5px] font-mono uppercase tracking-wider text-ink-3">
          {member.role === 'captain'
            ? t('admin:team.admin.detail.roster_role_captain')
            : t('admin:team.admin.detail.roster_role_member')}
        </div>
      </div>
    </li>
  );
}

export function AdminTeamDetailPage() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const toast = useToast();
  const auth = useAuth();

  const canModerate = useMemo(() => {
    return auth.adminUser?.permissions.includes('teams:moderate') ?? false;
  }, [auth.adminUser]);

  const [detail, setDetail] = useState<AdminTeamDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-dialog state.
  const [suspendOpen, setSuspendOpen] = useState(false);
  const [reinstateOpen, setReinstateOpen] = useState(false);
  const [disbandOpen, setDisbandOpen] = useState(false);

  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const fetchDetail = useCallback(async () => {
    if (!teamId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await adminTeamsApi.detail(teamId);
      setDetail(data);
    } catch (err) {
      if (err instanceof ApiError && err.body?.error?.code === 'TEAM_NOT_FOUND') {
        setError(t('admin:team.admin.detail.error_not_found'));
      } else {
        setError(translateApiError(err, t));
      }
    } finally {
      setLoading(false);
    }
  }, [teamId, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const handleSuspend = async (body: SuspendTeamInput) => {
    if (!teamId || !detail) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminTeamsApi.suspend(teamId, body);
      toast.push({
        tone: 'success',
        title: t('admin:team.admin.suspend_dialog.toast_success', {
          teamName: detail.team.name,
        }),
      });
      setSuspendOpen(false);
      await fetchDetail();
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const handleReinstate = async (body: ReinstateTeamInput) => {
    if (!teamId || !detail) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminTeamsApi.reinstate(teamId, body);
      toast.push({
        tone: 'success',
        title: t('admin:team.admin.reinstate_dialog.toast_success', {
          teamName: detail.team.name,
        }),
      });
      setReinstateOpen(false);
      await fetchDetail();
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisband = async (body: DisbandTeamByAdminInput) => {
    if (!teamId || !detail) return;
    setActionLoading(true);
    setActionError(null);
    try {
      await adminTeamsApi.forceDisband(teamId, body);
      toast.push({
        tone: 'success',
        title: t('admin:team.admin.force_disband_dialog.toast_success', {
          teamName: detail.team.name,
        }),
      });
      setDisbandOpen(false);
      await fetchDetail();
    } catch (err) {
      setActionError(translateApiError(err, t));
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="team-detail-loading">
        <Skeleton className="h-8 w-48 rounded-md" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-48 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={() => navigate('/teams')}>
          ← {t('admin:team.admin.detail.back_to_queue')}
        </Button>
        <EmptyState
          icon={<UIIcon name="flag" size={36} color="currentColor" />}
          title={t('admin:team.admin.detail.error_title')}
          description={error ?? ''}
        />
      </div>
    );
  }

  const { team, captain, members, recentAuditEntries, suspendedByAdminDisplayName } = detail;
  const isActive = team.status === 'active';
  const isSuspended = team.status === 'suspended';
  const isDisbanded = team.status === 'disbanded';

  const auditEvents: AuditEvent[] = recentAuditEntries.map((e) => mapAuditEntry(e, t, locale));

  return (
    <div className="space-y-6">
      <div>
        <Button variant="ghost" size="sm" onClick={() => navigate('/teams')}>
          ← {t('admin:team.admin.detail.back_to_queue')}
        </Button>
      </div>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-1">
          <h1 className="text-h1 font-bold text-ink" data-testid="team-detail-title">
            {(() => {
              const interpolated = t('admin:team.admin.detail_title', {
                teamName: team.name,
                defaultValue: 'Team: {teamName}',
              }) as string;
              return interpolated.includes('{teamName}') ? `Team: ${team.name}` : interpolated;
            })()}
          </h1>
          <p className="text-[13px] text-ink-3" data-testid="team-detail-subtitle">
            {(() => {
              const interpolated = t('admin:team.admin.detail.header_subtitle', {
                createdBy: captain?.displayName ?? '—',
                createdAt: formatDate(team.createdAt, locale),
                defaultValue: 'Created by {createdBy} on {createdAt}',
              }) as string;
              return interpolated.includes('{createdBy}') || interpolated.includes('{createdAt}')
                ? `Created by ${captain?.displayName ?? '—'} on ${formatDate(team.createdAt, locale)}`
                : interpolated;
            })()}
          </p>
        </div>
        <StatusChip
          status={STATUS_CHIP_MAP[team.status]}
          label={t(`team:status.${team.status}`)}
          pulse={isSuspended}
        />
      </div>

      {/* State-aware banner (wrapped in <div> so testid forwards reliably —
          Banner's primitive prop surface doesn't accept data-testid).
          ICU `{var}` interpolation is unreliable in test env, so the body
          string is rendered with the same defensive guard pattern F23 uses. */}
      {isSuspended ? (
        <div data-testid="suspended-banner">
          <Banner tone="warning" title={t('admin:team.admin.detail.suspended_banner_title')}>
            {(() => {
              const suspendedAt = formatDate(team.suspendedAt, locale);
              const reason = team.suspendedReason ?? '—';
              if (suspendedByAdminDisplayName) {
                const raw = t('admin:team.admin.detail.suspended_banner_body', {
                  adminName: suspendedByAdminDisplayName,
                  suspendedAt,
                  reason,
                  defaultValue: 'Suspended by {adminName} on {suspendedAt}. Reason: {reason}',
                }) as string;
                return raw.includes('{adminName}') ||
                  raw.includes('{suspendedAt}') ||
                  raw.includes('{reason}')
                  ? `Suspended by ${suspendedByAdminDisplayName} on ${suspendedAt}. Reason: ${reason}`
                  : raw;
              }
              const raw = t('admin:team.admin.detail.suspended_banner_body_no_admin', {
                suspendedAt,
                reason,
                defaultValue: 'Suspended on {suspendedAt}. Reason: {reason}',
              }) as string;
              return raw.includes('{suspendedAt}') || raw.includes('{reason}')
                ? `Suspended on ${suspendedAt}. Reason: ${reason}`
                : raw;
            })()}
          </Banner>
        </div>
      ) : null}

      {isDisbanded ? (
        <div data-testid="disbanded-banner">
          <Banner tone="danger" title={t('admin:team.admin.detail.disbanded_banner_title')}>
            {(() => {
              const disbandedAt = formatDate(team.disbandedAt, locale);
              const raw = t('admin:team.admin.detail.disbanded_banner_body', {
                disbandedAt,
                defaultValue:
                  'This team was disbanded on {disbandedAt}. No further moderation actions are possible.',
              }) as string;
              return raw.includes('{disbandedAt}')
                ? `This team was disbanded on ${disbandedAt}. No further moderation actions are possible.`
                : raw;
            })()}
          </Banner>
        </div>
      ) : null}

      {/* Team header card */}
      <TeamCard
        name={team.name}
        sport={team.sportName}
        memberCount={team.memberCount}
        {...(team.averageSkillRating !== null ? { averageSkill: team.averageSkillRating } : {})}
        {...(captain
          ? {
              captain: {
                name: captain.displayName,
                ...(captain.avatarUrl ? { avatarSrc: captain.avatarUrl } : {}),
                isCaptain: true,
              },
            }
          : {})}
        members={members.map((m) => {
          const base: { name: string; isCaptain: boolean; avatarSrc?: string } = {
            name: m.displayName,
            isCaptain: m.role === 'captain',
          };
          if (m.avatarUrl) base.avatarSrc = m.avatarUrl;
          return base;
        })}
        {...(team.homeCazaCode ? { homeCaza: team.homeCazaCode } : {})}
      />

      {/* Action bar — gated on `teams:moderate` */}
      {canModerate && !isDisbanded ? (
        <div className="flex flex-wrap gap-2" data-testid="team-action-bar">
          {isActive ? (
            <>
              <Button
                variant="secondary"
                onClick={() => {
                  setActionError(null);
                  setSuspendOpen(true);
                }}
                data-testid="open-suspend-dialog"
              >
                {t('admin:team.admin.detail.action_suspend')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setActionError(null);
                  setDisbandOpen(true);
                }}
                data-testid="open-disband-dialog"
              >
                {t('admin:team.admin.detail.action_disband')}
              </Button>
            </>
          ) : null}
          {isSuspended ? (
            <>
              <Button
                variant="primary"
                onClick={() => {
                  setActionError(null);
                  setReinstateOpen(true);
                }}
                data-testid="open-reinstate-dialog"
              >
                {t('admin:team.admin.detail.action_reinstate')}
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setActionError(null);
                  setDisbandOpen(true);
                }}
                data-testid="open-disband-dialog"
              >
                {t('admin:team.admin.detail.action_disband')}
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {/* Roster */}
      <Card>
        <h2 className="text-h2 font-semibold text-ink mb-3">
          {t('admin:team.admin.detail.section_roster')}
        </h2>
        {members.length === 0 ? (
          <p className="text-[12.5px] text-ink-3">{t('admin:team.admin.detail.roster_empty')}</p>
        ) : (
          <ul className="m-0 p-0 list-none" data-testid="team-roster-list">
            {members.map((m) => (
              <RosterRow key={m.id} member={m} t={t} />
            ))}
          </ul>
        )}
      </Card>

      {/* Audit log */}
      <Card>
        <h2 className="text-h2 font-semibold text-ink mb-3">
          {t('admin:team.admin.detail.section_audit')}
        </h2>
        {auditEvents.length === 0 ? (
          <p className="text-[12.5px] text-ink-3">{t('admin:team.admin.detail.audit_empty')}</p>
        ) : (
          <AuditLogTimeline events={auditEvents} />
        )}
      </Card>

      {/* Dialogs */}
      <SuspendTeamDialog
        open={suspendOpen}
        teamName={team.name}
        loading={actionLoading}
        serverError={actionError}
        onClose={() => setSuspendOpen(false)}
        onSubmit={handleSuspend}
      />
      <ReinstateTeamDialog
        open={reinstateOpen}
        teamName={team.name}
        loading={actionLoading}
        serverError={actionError}
        onClose={() => setReinstateOpen(false)}
        onSubmit={handleReinstate}
      />
      <DisbandTeamDialog
        open={disbandOpen}
        teamName={team.name}
        loading={actionLoading}
        serverError={actionError}
        onClose={() => setDisbandOpen(false)}
        onSubmit={handleDisband}
      />
    </div>
  );
}
